import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Qr } from '@/shared/ui/Qr'
import { Strip } from '@/shared/ui/Strip'
import { CopyIcon } from '@/shared/ui/icons'
import type { Currency } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { compareDecimal } from '@/shared/lib/decimal'
import { crypto } from '@/shared/lib/format'
import { useStores } from '@/stores/RootStore'
import { DialogPage } from './DialogPage'
import styles from './Receive.module.css'

/**
 * Получение средств: адрес пополнения аккаунта.
 *
 * Суммы на экране нет, и это по макету: мерчант отдаёт адрес
 * плательщику, а сколько тот отправит — решает он сам. Сколько придёт,
 * столько и зачислится. Заявка с фиксированной суммой — другая
 * сущность: она живёт на странице оплаты и создаётся через API.
 */
export const ReceivePage = observer(function ReceivePage({ currency }: { currency: Currency }) {
  const { receive: store } = useStores()

  useEffect(() => { store.start(currency) }, [currency, store])

  const coin = coinOf(currency)
  const network = networkOf(currency)
  // Минимум есть только у USDT-TRC20: меньше него пополнение целиком уходит на свод.
  const minimum = store.details?.min_amount
  const minNote = minimum && (compareDecimal(minimum, '0') ?? 0) > 0
    ? `от ${crypto(minimum)} ${coin.ticker}`
    : undefined

  return (
    <DialogPage title="Получить" note={`Получение ${coin.ticker} в сети ${network}.`}
      /* Главное действие в подвале рядом с «Закрыть» — так в макете.
         Адрес человек копирует, а не переписывает с экрана: переписанный
         руками адрес — это перевод в никуда. */
      actions={
        <Button variant="primary" disabled={store.address === ''}
                onClick={() => void store.copy(store.address, 'Адрес скопирован')}>
          Скопировать адрес
        </Button>
      }
    >
      <>
        <Strip tone="warn">
          Отправляйте только {coin.ticker} в сети {network}.
          Другая сеть приведёт к потере средств.
        </Strip>

        <div className={styles.fields}>
          <Field label="Адрес для пополнения"
                 value={store.address}
                 placeholder={store.busy ? 'Выдаём адрес…' : ' '}
                 readOnly
                 error={store.error}
                 icon={
                   <button type="button" className={styles.copy}
                           aria-label="Скопировать адрес"
                           onClick={() => void store.copy(store.address, 'Адрес скопирован')}>
                     <CopyIcon />
                   </button>
                 } />

          {/* Сеть не выбирается — она определена валютой. Выключенное поле,
              как в макете: видно, что это значение, и что менять его нельзя. */}
          <Field label="Сеть" value={network} disabled note={minNote} />

          {/* Код в рамке с подписью — как в макете. В коде голый адрес:
              суммы у пополнения нет, и подставлять в код нечего. */}
          <div className={styles.qrBlock}>
            <span className={styles.qrLabel}>QR-код</span>
            <div className={styles.qrWrap}>
              {store.address === '' ? null : <Qr value={store.address} className={styles.qr} />}
            </div>
          </div>
        </div>
      </>
    </DialogPage>
  )
})
